import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { Alert, AlertType } from '../alert';
import { AlertService } from '../alert.service';
import { trigger, transition, style, animate } from '@angular/animations';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgFor } from '@angular/common';
import { Subscription } from 'rxjs';

/** An alert on screen, plus the animation state and dismiss timer that belong to that one card. */
interface DisplayedAlert {
  alert: Alert;
  state: 'fadeIn' | 'fadeOut';
  timer: ReturnType<typeof setTimeout> | null;
}

@Component({
    selector: 'cub-alert',
    templateUrl: './alert.component.html',
    styleUrls: ['./alert.component.scss'],
    animations: [
        trigger('someCoolAnimation', [
            transition('* => fadeIn', [
                style({ opacity: 0, marginTop: -100 }),
                animate(500, style({ opacity: 1, marginTop: 10 }))
            ]),
            transition('* => fadeOut', [
                animate(500, style({ opacity: 0, marginTop: -100 }))
            ])
        ])
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgFor]
})

export class AlertComponent implements OnInit, OnDestroy {
  displayedAlerts: DisplayedAlert[] = [];

  private subscription: Subscription;

  constructor(
    private alertService: AlertService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.subscription = this.alertService.getAlert().subscribe((alert: Alert) => {
      if (!alert) {
        // clear alerts when an empty alert is received
        this.clearAll();
        return;
      }

      if (alert.type === AlertType.Success) {
        // Top-center rather than the Material default of bottom-center: at the bottom of a tall,
        // scrolling detail screen the confirmation sits well outside where the user is looking.
        this.snackBar.open(alert.message, 'Ok', {
          duration: AlertComponent.durationFor(AlertType.Success),
          verticalPosition: 'top',
          horizontalPosition: 'center',
        });
        return;
      }

      // The version notice has its own banner component; it must not also appear in this stack.
      if (alert.type === AlertType.Version) {
        return;
      }

      const displayed: DisplayedAlert = { alert, state: 'fadeIn', timer: null };
      this.displayedAlerts.push(displayed);
      this.startTimer(displayed);
    });
  }

  ngOnDestroy() {
    this.clearAll();
    this.subscription?.unsubscribe();
  }

  /**
   * How long each level stays on screen before dismissing itself. Errors get the longest run because
   * several of them are two full sentences the user has to act on; warnings are advisory and shorter;
   * success keeps the two seconds it already had on the snackbar.
   *
   * <p>Before this, only success auto-closed — everything else sat in the stack until it was clicked
   * or the route changed, which on a screen like disposition detail meant cards piling up.</p>
   */
  private static durationFor(type: AlertType): number {
    switch (type) {
      case AlertType.Error:
        return 10000;
      case AlertType.Warning:
        return 5000;
      case AlertType.Info:
        return 3000;
      default:
        return 2000;
    }
  }

  /** Dismisses the card, letting the fade-out play; the animation callback removes it. */
  dismiss(displayed: DisplayedAlert) {
    this.stopTimer(displayed);
    displayed.state = 'fadeOut';
  }

  /**
   * A card is only removed once its own fade-out has finished. This fires for the fade-in too, hence
   * the state check — and each card carries its own state, so dismissing one no longer animates the
   * rest of the stack out with it.
   */
  onAnimationDone(displayed: DisplayedAlert) {
    if (displayed.state === 'fadeOut') {
      this.displayedAlerts = this.displayedAlerts.filter(d => d !== displayed);
    }
  }

  /**
   * Hovering holds the card open, and the full duration restarts on the way out. Without it, a ten
   * second error can still expire mid-sentence on the one occasion someone is actually reading it.
   */
  hold(displayed: DisplayedAlert) {
    this.stopTimer(displayed);
  }

  release(displayed: DisplayedAlert) {
    if (displayed.state !== 'fadeOut') {
      this.startTimer(displayed);
    }
  }

  cssClass(alert: Alert): string {
    if (!alert) {
      return '';
    }

    // return css class based on alert type
    switch (alert.type) {
      case AlertType.Success:
        return 'alert alert-success';
      case AlertType.Error:
        return 'alert alert-danger';
      case AlertType.Info:
        return 'alert alert-success';
      case AlertType.Warning:
        return 'alert alert-warning';
      default:
        return '';
    }
  }

  private startTimer(displayed: DisplayedAlert) {
    this.stopTimer(displayed);
    displayed.timer = setTimeout(() => this.dismiss(displayed),
      AlertComponent.durationFor(displayed.alert.type));
  }

  private stopTimer(displayed: DisplayedAlert) {
    if (displayed.timer) {
      clearTimeout(displayed.timer);
      displayed.timer = null;
    }
  }

  /**
   * Drops everything immediately, no fade — this runs on a route change and on destroy, where the
   * cards are going away with the page anyway. Timers are cleared so none of them fires against a
   * component that is no longer on screen.
   */
  private clearAll() {
    this.displayedAlerts.forEach(displayed => this.stopTimer(displayed));
    this.displayedAlerts = [];
  }
}
